import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import {
  Users, UserCheck, CreditCard, AlertTriangle, Activity,
  CheckCircle2, XCircle, Zap, Link2, RefreshCw, ArrowRight,
  Eye, Globe, MousePointerClick, TrendingUp, ExternalLink,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

function Stat({ icon: Icon, label, value, tone = 'neutral', testid, to }) {
  const tones = {
    neutral: 'border-white/10',
    ok: 'border-emerald-500/40',
    warn: 'border-amber-500/40',
    bad: 'border-red-500/40',
    accent: 'border-[#d8b34a]/40 bg-[#13110a]',
  };
  const iconColor = {
    neutral: 'text-zinc-500',
    ok: 'text-emerald-400',
    warn: 'text-amber-400',
    bad: 'text-red-400',
    accent: 'gold-text',
  }[tone];

  const inner = (
    <div className={`group border ${tones[tone]} rounded-md p-5 bg-gradient-to-br from-[#0e0e10] to-[#0a0a0c] transition-all duration-200 hover:-translate-y-0.5`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">{label.toUpperCase()}</p>
        <Icon size={14} className={iconColor} strokeWidth={1.8} />
      </div>
      <p className="mt-4 text-3xl lg:text-4xl font-display font-bold tabular-nums tracking-tight text-white">
        {value ?? '—'}
      </p>
    </div>
  );

  if (to) {
    return <Link to={to} data-testid={testid}>{inner}</Link>;
  }
  return <div data-testid={testid}>{inner}</div>;
}

export default function AdminHome() {
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get('/admin/summary'),
        api.get('/admin/analytics/summary?days=14').catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setAnalytics(a.data);
    } catch (e) {
      console.error('[admin/summary] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const u = summary?.users || {};
  const t = summary?.today || {};
  const at = analytics?.today || {};
  const aw = analytics?.week || {};

  return (
    <AdminLayout title="Overview" description="Real-time platform snapshot — user states, signal flow, and execution health for today.">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-zinc-400 font-mono" data-testid="admin-summary-as-of">
          {loading ? 'Loading…' : summary?.as_of ? `As of ${new Date(summary.as_of).toLocaleString()}` : ''}
        </p>
        <button onClick={load} data-testid="admin-summary-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Users panel */}
      <section data-testid="admin-summary-users-section">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-px w-6 bg-[#d8b34a]" />
          <p className="text-xs tracking-[0.4em] font-mono gold-text">USERS</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Total" value={u.total} tone="accent" testid="stat-total-users" to="/admin/users" />
          <Stat icon={UserCheck} label="Beta" value={u.beta} testid="stat-beta-users" to="/admin/users?status=beta" />
          <Stat icon={UserCheck} label="Free" value={u.free} testid="stat-free-users" />
          <Stat icon={CreditCard} label="Paid" value={u.paid} tone="ok" testid="stat-paid-users" />
          <Stat icon={XCircle} label="Canceled" value={u.canceled} tone="warn" testid="stat-canceled-users" />
          <Stat icon={AlertTriangle} label="Suspended" value={u.suspended} tone="bad" testid="stat-suspended-users" />
          <Stat icon={Link2} label="Alpaca Connected" value={u.alpaca_connected} tone="ok" testid="stat-alpaca-connected" />
          <Stat icon={Zap} label="Auto-Trading On" value={u.paper_auto_trading} tone="accent" testid="stat-auto-trading" />
        </div>
      </section>

      {/* Traffic & conversions */}
      <section className="mt-10" data-testid="admin-traffic-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="h-px w-6 bg-[#d8b34a]" />
            <p className="text-xs tracking-[0.4em] font-mono gold-text">TRAFFIC · LAST 7 DAYS</p>
          </div>
          <Link to="/admin/analytics" data-testid="admin-traffic-deeplink" className="text-[11px] font-mono text-zinc-500 hover:gold-text inline-flex items-center gap-1">
            GA4 deep-dive <ExternalLink size={11} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          <Stat icon={Eye} label="Visits today" value={at.visits} tone="accent" testid="stat-visits-today" />
          <Stat icon={Users} label="Unique visitors" value={at.unique_visitors} testid="stat-unique-today" />
          <Stat icon={MousePointerClick} label="Sign-up clicks" value={at.signup_clicks} testid="stat-signup-clicks-today" />
          <Stat icon={CheckCircle2} label="Sign-ups" value={at.signups_completed} tone="ok" testid="stat-signups-today" />
          <Stat
            icon={TrendingUp}
            label="Sign-up conv %"
            value={aw.signup_conversion_pct != null ? `${aw.signup_conversion_pct}%` : '—'}
            tone={aw.signup_conversion_pct && aw.signup_conversion_pct >= 5 ? 'ok' : 'warn'}
            testid="stat-signup-conv-pct"
          />
        </div>

        {/* Sparkline + top tables */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 border border-white/10 rounded-md bg-[#0c0c0e] p-5" data-testid="traffic-chart-card">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">VISITS PER DAY · LAST 14 DAYS</p>
              <p className="text-[10px] font-mono text-zinc-600">Self-hosted · auto-purges {analytics?.ttl_days || 90}d</p>
            </div>
            <div className="h-48">
              {analytics?.daily?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.daily} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f22" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#111114', border: '1px solid #2a2a2e', borderRadius: 4, fontSize: 12 }}
                      labelStyle={{ color: '#e4e4e7' }}
                      itemStyle={{ color: '#d8b34a' }}
                    />
                    <Line type="monotone" dataKey="visits" stroke="#d8b34a" strokeWidth={2} dot={{ r: 2, fill: '#d8b34a' }} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-xs text-zinc-600">No traffic data yet — visits will appear once events fire.</div>
              )}
            </div>
          </div>

          <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5" data-testid="top-pages-card">
            <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500 mb-3">TOP PAGES · LAST 7 DAYS</p>
            <ul className="space-y-2.5">
              {(analytics?.top_pages || []).map((p) => (
                <li key={p.path} className="flex items-center justify-between gap-2 text-xs">
                  <code className="font-mono text-zinc-200 truncate" title={p.path}>{p.path}</code>
                  <span className="text-zinc-500 tabular-nums">{p.visits}</span>
                </li>
              ))}
              {!analytics?.top_pages?.length && <li className="text-xs text-zinc-600">No page views yet.</li>}
            </ul>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 mt-4">
          <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5" data-testid="top-referrers-card">
            <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500 mb-3">TOP REFERRERS · LAST 7 DAYS</p>
            <ul className="space-y-2.5">
              {(analytics?.top_referrers || []).map((r) => (
                <li key={r.referrer} className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-mono text-zinc-200 truncate flex items-center gap-2"><Globe size={11} className="gold-text" />{r.referrer}</span>
                  <span className="text-zinc-500 tabular-nums">{r.visits}</span>
                </li>
              ))}
              {!analytics?.top_referrers?.length && <li className="text-xs text-zinc-600">No referrers tracked yet — most traffic is likely direct.</li>}
            </ul>
          </div>

          <div className="border border-white/10 rounded-md bg-[#0c0c0e] p-5" data-testid="conversion-funnel-card">
            <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500 mb-3">CONVERSION FUNNEL · LAST 7 DAYS</p>
            <ul className="space-y-3 text-xs">
              {[
                { k: 'sign_up_click', label: 'Sign-up button clicks' },
                { k: 'signup_complete', label: 'Sign-ups completed' },
                { k: 'login_complete', label: 'Logins completed' },
                { k: 'alpaca_connected', label: 'Alpaca connections' },
                { k: 'beta_invite_click', label: 'Beta invite clicks' },
                { k: 'beta_invite_verified', label: 'Beta invites verified' },
              ].map((row, idx, arr) => {
                const v = aw[row.k] ?? 0;
                const max = Math.max(...arr.map(r => aw[r.k] ?? 0), 1);
                const w = Math.round((v / max) * 100);
                return (
                  <li key={row.k}>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-300">{row.label}</span>
                      <span className="text-zinc-400 tabular-nums">{v}</span>
                    </div>
                    <div className="mt-1 h-1 bg-white/5 rounded-sm overflow-hidden">
                      <div className="h-full bg-[#d8b34a]" style={{ width: `${w}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Today panel */}
      <section className="mt-10" data-testid="admin-summary-today-section">
        <div className="flex items-center gap-2 mb-4">
          <span className="h-px w-6 bg-[#d8b34a]" />
          <p className="text-xs tracking-[0.4em] font-mono gold-text">TODAY</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <Stat icon={Activity} label="Signals Today" value={t.signals} testid="stat-signals-today" to="/admin/signals" />
          <Stat icon={Activity} label="Bot Signals" value={t.bot_signals} tone="accent" testid="stat-bot-signals-today" />
          <Stat icon={Zap} label="Executions" value={t.executions} testid="stat-executions-today" to="/admin/executions" />
          <Stat icon={CheckCircle2} label="Filled" value={t.filled} tone="ok" testid="stat-filled-today" to="/admin/trades" />
          <Stat icon={Activity} label="Submitted" value={t.submitted} tone="warn" testid="stat-submitted-today" />
          <Stat icon={AlertTriangle} label="Skipped" value={t.skipped} testid="stat-skipped-today" />
          <Stat icon={XCircle} label="Failed/Expired" value={t.failed} tone="bad" testid="stat-failed-today" />
        </div>
      </section>

      {/* Quick links */}
      <section className="mt-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/admin/users" className="group border border-white/10 rounded-md p-5 bg-[#0c0c0e] hover:border-[#d8b34a]/40 transition-all flex items-center justify-between" data-testid="quick-link-users">
            <div>
              <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">MANAGE</p>
              <p className="mt-1 text-base font-medium text-white">User accounts</p>
            </div>
            <ArrowRight size={16} className="text-zinc-500 group-hover:gold-text group-hover:translate-x-1 transition-all" />
          </Link>
          <Link to="/admin/trades" className="group border border-white/10 rounded-md p-5 bg-[#0c0c0e] hover:border-[#d8b34a]/40 transition-all flex items-center justify-between" data-testid="quick-link-trades">
            <div>
              <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">REVIEW</p>
              <p className="mt-1 text-base font-medium text-white">Trade ledger</p>
            </div>
            <ArrowRight size={16} className="text-zinc-500 group-hover:gold-text group-hover:translate-x-1 transition-all" />
          </Link>
          <Link to="/admin/billing" className="group border border-white/10 rounded-md p-5 bg-[#0c0c0e] hover:border-[#d8b34a]/40 transition-all flex items-center justify-between" data-testid="quick-link-billing">
            <div>
              <p className="text-[10px] tracking-[0.3em] font-mono text-zinc-500">CONFIGURE</p>
              <p className="mt-1 text-base font-medium text-white">Billing & plans</p>
            </div>
            <ArrowRight size={16} className="text-zinc-500 group-hover:gold-text group-hover:translate-x-1 transition-all" />
          </Link>
        </div>
      </section>
    </AdminLayout>
  );
}
